/**
 * SPARC-002: Opportunity pipeline Kanban board
 * Stages: New → Evaluating → Shortlisted → Pursuing → Won/Lost
 * Pattern: StageGateKanban (HTML5 drag-and-drop, admin-gated transitions)
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Badge, Button, LoadingSpinner } from '@tracekit/core';
import { Building2, ChevronLeft, ChevronRight, Clock, GripVertical, Inbox, List, User, X } from 'lucide-react';
import type { GovtOpportunityEntity } from '../../pages/government-data/OpportunitiesDashboard';
import {
  formatDeadlineDaysLabel,
  formatOpportunityDeadlineDate,
  getDeadlineUrgency,
  getDeadlineUrgencyClasses,
} from '../../utils/opportunityUtils';
import { MatchScoreTooltip, type MatchExplain } from './MatchScoreTooltip';
import {
  BD_PRIORITY_BADGE_CLASS,
  BD_PRIORITY_OPTIONS,
  getEffectiveBdPriority,
  isDefaultPriority,
  isManualBdPriority,
  resolveKanbanMatchScore,
  visibleKanbanColumns,
  type BdPriority,
  type PipelineKanbanColumn,
  type PipelineStage,
  columnIdForStage,
  defaultStageForColumn,
  resolveEffectivePipelineStage,
  KANBAN_COLUMN_PAGE_SIZE,
} from '../../types/opportunityPipeline';

export interface PipelineKanbanOpportunity {
  id: string;
  title: string;
  department?: string;
  daysUntil: number | null;
  deadline?: string | null;
  matchScore: number | null;
  matchExplain: MatchExplain | null;
  priority: BdPriority | null;
  isDefaultPriority: boolean;
  manualPriority: BdPriority | null;
  assignedBd?: string | null;
  pipelineStage: PipelineStage;
  raw: GovtOpportunityEntity;
}

interface OpportunityPipelineKanbanProps {
  opportunities: GovtOpportunityEntity[];
  onOpportunityClick?: (opp: GovtOpportunityEntity) => void;
  onStageChange?: (entityId: string, newStage: PipelineStage) => void;
  onPriorityChange?: (entityId: string, priority: BdPriority | null) => void;
  onDiscard?: (entityId: string) => void;
  canChangeStage?: boolean;
  canChangePriority?: boolean;
  isLoading?: boolean;
  isTransitioning?: boolean;
  isPriorityUpdating?: boolean;
  priorityUpdatingId?: string | null;
  discardingId?: string | null;
  /** When false (default), the Discarded column is hidden. */
  showDiscarded?: boolean;
  /** Cards shown per column before Load More. */
  columnPageSize?: number;
  /** Switch the dashboard to list view from an empty New column. */
  onViewList?: () => void;
}

function urgencyBorderClass(daysUntil: number | null): string {
  const urgency = getDeadlineUrgency(daysUntil);
  switch (urgency) {
    case 'urgent':
    case 'expired':
      return 'border-l-4 border-l-red-500';
    case 'soon':
      return 'border-l-4 border-l-yellow-500';
    default:
      return 'border-l-4 border-l-green-500';
  }
}

function toKanbanOpportunity(opp: GovtOpportunityEntity): PipelineKanbanOpportunity {
  const data = opp.data as Record<string, unknown>;
  const daysUntil =
    opp.data.days_until_deadline ??
    (typeof data.deadline === 'string'
      ? Math.ceil((new Date(data.deadline as string).getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      : null);

  const manualPriority = isManualBdPriority(data.bd_priority) ? data.bd_priority : null;
  const effectivePriority = getEffectiveBdPriority(data);
  const deadline = typeof data.deadline === 'string' ? data.deadline : null;

  return {
    id: opp.id,
    title: opp.data.title || 'Untitled opportunity',
    department:
      opp.data.organization ||
      opp.data.issuing_agency ||
      opp.data.department ||
      undefined,
    daysUntil: daysUntil ?? null,
    deadline,
    matchScore: resolveKanbanMatchScore(data),
    matchExplain: (data.match_explain as MatchExplain | undefined) ?? null,
    priority: effectivePriority,
    isDefaultPriority: isDefaultPriority(data),
    manualPriority,
    assignedBd: (data.assigned_bd_person as string) || null,
    pipelineStage: resolveEffectivePipelineStage(data),
    raw: opp,
  };
}

function KanbanCardSkeleton() {
  return (
    <div className="p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg animate-pulse">
      <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-3" />
      <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2" />
      <div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-20 mb-2" />
      <div className="h-10 bg-gray-200 dark:bg-gray-700 rounded w-full" />
    </div>
  );
}

function KanbanBoardSkeleton() {
  const skeletonColumns = visibleKanbanColumns(false);

  return (
    <div className="w-full">
      <div className="flex items-center justify-center gap-2 py-3 mb-2 text-sm text-theme-text-secondary">
        <LoadingSpinner size="sm" />
        <span>Loading pipeline opportunities…</span>
      </div>
      <div className="flex gap-4 overflow-x-auto pb-4">
        {skeletonColumns.map((column) => (
          <div key={column.id} className="flex-shrink-0 w-80" style={{ minWidth: '320px' }}>
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                <div className={`w-3 h-3 rounded-full ${column.color} opacity-50`} />
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse" />
                <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-6 animate-pulse" />
              </div>
            </div>
            <div className="space-y-3 min-h-[240px] rounded-lg p-2 bg-gray-50/50 dark:bg-gray-900/30">
              {[1, 2, 3].map((i) => (
                <KanbanCardSkeleton key={i} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export const OpportunityPipelineKanban: React.FC<OpportunityPipelineKanbanProps> = ({
  opportunities,
  onOpportunityClick,
  onStageChange,
  onPriorityChange,
  onDiscard,
  canChangeStage = false,
  canChangePriority = false,
  isLoading = false,
  isTransitioning = false,
  isPriorityUpdating = false,
  priorityUpdatingId = null,
  discardingId = null,
  showDiscarded = false,
  columnPageSize = KANBAN_COLUMN_PAGE_SIZE,
  onViewList,
}) => {
  const [dragged, setDragged] = useState<PipelineKanbanOpportunity | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<PipelineKanbanColumn['id'] | null>(null);
  const [visibleByColumn, setVisibleByColumn] = useState<Record<string, number>>({});

  // SPARC-ISSUE-012: the board can be wider than the viewport (5-6 columns × 320px).
  // Track scroll position so we can show explicit prev/next controls and edge fades
  // instead of relying on users to discover an implicit `overflow-x-auto` scrollbar —
  // this was the root cause of the rightmost column appearing "cut off"/broken.
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const cards = useMemo(() => opportunities.map(toKanbanOpportunity), [opportunities]);

  const columns = useMemo(() => {
    return visibleKanbanColumns(showDiscarded).map((column) => ({
      ...column,
      opportunities: cards.filter((c) => column.stages.includes(c.pipelineStage)),
    }));
  }, [cards, showDiscarded]);

  useEffect(() => {
    setVisibleByColumn({});
  }, [opportunities]);

  const updateScrollButtons = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft < maxScroll - 4);
  }, []);

  useEffect(() => {
    updateScrollButtons();
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(updateScrollButtons);
    observer.observe(el);
    return () => observer.disconnect();
  }, [updateScrollButtons, columns]);

  const scrollByColumn = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const columnWidth = 320 + 16; // w-80 + gap-4
    el.scrollBy({ left: direction === 'left' ? -columnWidth : columnWidth, behavior: 'smooth' });
  };

  const handleDragStart = (e: React.DragEvent, opp: PipelineKanbanOpportunity) => {
    if (!canChangeStage || isTransitioning) {
      e.preventDefault();
      return;
    }
    setDragged(opp);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', opp.id);
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDragged(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e: React.DragEvent, columnId: PipelineKanbanColumn['id']) => {
    if (!canChangeStage || !dragged || isTransitioning) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumn(columnId);
  };

  const handleDrop = (e: React.DragEvent, column: PipelineKanbanColumn) => {
    e.preventDefault();
    if (!dragged || !canChangeStage || isTransitioning) return;

    const targetStage = defaultStageForColumn(column.id);
    const currentColumn = columnIdForStage(dragged.pipelineStage);

    if (currentColumn === column.id) {
      setDragged(null);
      setDragOverColumn(null);
      return;
    }

    if (onStageChange && dragged.pipelineStage !== targetStage) {
      onStageChange(dragged.id, targetStage);
    }

    setDragged(null);
    setDragOverColumn(null);
  };

  if (isLoading) {
    return <KanbanBoardSkeleton />;
  }

  return (
    <div className="w-full min-w-0 max-w-full">
      {!canChangeStage && (
        <p className="text-xs text-theme-text-secondary mb-3">
          View-only mode — admin role required to drag cards between stages.
        </p>
      )}
      <div className="relative w-full min-w-0 max-w-full">
        {/* Left/right scroll controls — explicit affordance so the board never
            looks "stuck" with a column half-visible at the edge (SPARC-ISSUE-012). */}
        {canScrollLeft && (
          <button
            type="button"
            aria-label="Scroll columns left"
            onClick={() => scrollByColumn('left')}
            className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-theme-border bg-theme-paper p-1.5 shadow-md hover:bg-theme-surface-hover transition-colors"
          >
            <ChevronLeft className="h-4 w-4 text-theme-text" />
          </button>
        )}
        {canScrollRight && (
          <button
            type="button"
            aria-label="Scroll columns right"
            onClick={() => scrollByColumn('right')}
            className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full border border-theme-border bg-theme-paper p-1.5 shadow-md hover:bg-theme-surface-hover transition-colors"
          >
            <ChevronRight className="h-4 w-4 text-theme-text" />
          </button>
        )}
        {/* Edge fades hint that more columns exist off-screen. */}
        {canScrollLeft && (
          <div className="pointer-events-none absolute left-0 top-0 z-[5] h-full w-8 bg-gradient-to-r from-theme-paper to-transparent" />
        )}
        {canScrollRight && (
          <div className="pointer-events-none absolute right-0 top-0 z-[5] h-full w-8 bg-gradient-to-l from-theme-paper to-transparent" />
        )}
        <div
          ref={scrollRef}
          onScroll={updateScrollButtons}
          className="flex gap-4 overflow-x-auto pb-4 w-full min-w-0 max-w-full scroll-smooth"
        >
          {columns.map((column) => (
            <div
              key={column.id}
              className="flex-shrink-0 w-80"
              style={{ minWidth: '320px' }}
              onDragOver={(e) => handleDragOver(e, column.id)}
              onDragLeave={() => setDragOverColumn(null)}
              onDrop={(e) => handleDrop(e, column)}
            >
              <div className="mb-3">
                <div className="flex items-center gap-2 mb-1">
                  <span
                    className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-base font-bold ${column.badgeClass}`}
                  >
                    <span className={`h-2.5 w-2.5 rounded-full ${column.color}`} aria-hidden />
                    {column.title}
                  </span>
                  <Badge variant="default" className="text-sm font-semibold">
                    {column.opportunities.length}
                  </Badge>
                </div>
              </div>

              <div
                className={`space-y-3 min-h-[240px] rounded-lg p-2 transition-colors ${
                  dragOverColumn === column.id && canChangeStage
                    ? 'bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-300 dark:ring-blue-700'
                    : 'bg-gray-50/50 dark:bg-gray-900/30'
                }`}
              >
                {column.opportunities.slice(0, visibleByColumn[column.id] ?? columnPageSize).map((opp) => {
                const urgency = getDeadlineUrgency(opp.daysUntil);
                const urgencyClasses = getDeadlineUrgencyClasses(urgency);
                const isDiscarding = discardingId === opp.id;
                const showDiscardButton =
                  column.id === 'new' && canChangeStage && !!onDiscard && !isTransitioning;

                // Native div (not core Card): Card's `title` renders a visual header
                // and does not forward draggable/onClick — both caused broken layout
                // and dead interactions on Kanban cards (SPARC-ISSUE-012).
                const cardTitle =
                  !canChangeStage
                    ? 'Admin role required to move pipeline stage'
                    : isTransitioning
                      ? 'Saving stage change…'
                      : undefined;

                return (
                  <div
                    key={opp.id}
                    role="button"
                    tabIndex={0}
                    draggable={canChangeStage && !isTransitioning && !isDiscarding}
                    onDragStart={(e) => handleDragStart(e, opp)}
                    onDragEnd={handleDragEnd}
                    title={cardTitle}
                    className={`p-3 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-sm ${urgencyBorderClass(
                      opp.daysUntil
                    )} ${canChangeStage && !isTransitioning && !isDiscarding ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'} hover:shadow-md transition-shadow ${
                      isDiscarding ? 'opacity-60 pointer-events-none' : ''
                    }`}
                    onClick={() => onOpportunityClick?.(opp.raw)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onOpportunityClick?.(opp.raw);
                      }
                    }}
                  >
                    <div className="flex items-start gap-2 mb-2">
                      {/* Reserve grip width always so card body does not shift when
                          canChangeStage toggles between view-only and admin. */}
                      <span className="w-4 h-4 shrink-0 mt-0.5 inline-flex" aria-hidden>
                        {canChangeStage ? (
                          <GripVertical className="w-4 h-4 text-gray-400" />
                        ) : null}
                      </span>
                      <h4 className="text-sm font-semibold text-theme-text line-clamp-2 flex-1 min-w-0">
                        {opp.title}
                      </h4>
                      {showDiscardButton && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            onDiscard?.(opp.id);
                          }}
                          disabled={isDiscarding}
                          className="shrink-0 p-0.5 rounded text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 dark:hover:text-red-400 transition-colors"
                          title="Discard opportunity"
                          aria-label="Discard opportunity"
                        >
                          {isDiscarding ? (
                            <LoadingSpinner size="sm" className="w-4 h-4" />
                          ) : (
                            <X className="w-4 h-4" />
                          )}
                        </button>
                      )}
                    </div>

                    {opp.department && (
                      <div className="flex items-center gap-1 text-xs text-theme-text-secondary mb-2">
                        <Building2 className="w-3 h-3 shrink-0" />
                        <span className="truncate">{opp.department}</span>
                      </div>
                    )}

                    {opp.matchScore !== null && (
                      <div className="mb-2" onClick={(e) => e.stopPropagation()}>
                        <MatchScoreTooltip
                          score={opp.matchScore}
                          variant="badge"
                          explain={opp.matchExplain}
                        />
                      </div>
                    )}

                    <div
                      className="flex flex-wrap items-center gap-2 mb-2"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {opp.priority && (
                        <span
                          className={`text-xs font-semibold px-2 py-0.5 rounded ${BD_PRIORITY_BADGE_CLASS[opp.priority]} ${
                            opp.isDefaultPriority ? 'opacity-80 italic' : ''
                          }`}
                          title={opp.isDefaultPriority ? 'Default from tracking/pipeline status' : 'Manual priority'}
                        >
                          {opp.priority}
                          {opp.isDefaultPriority ? ' · auto' : ''}
                        </span>
                      )}
                      {canChangePriority && onPriorityChange && (
                        <select
                          value={opp.manualPriority || ''}
                          disabled={isPriorityUpdating && priorityUpdatingId === opp.id}
                          onChange={(e) => {
                            const value = e.target.value;
                            onPriorityChange(opp.id, value ? (value as BdPriority) : null);
                          }}
                          className="text-xs px-1.5 py-0.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-theme-text"
                          aria-label="BD priority"
                        >
                          <option value="">Auto</option>
                          {BD_PRIORITY_OPTIONS.map((p) => (
                            <option key={p} value={p}>
                              {p}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>

                    {opp.deadline && formatOpportunityDeadlineDate(opp.deadline) ? (
                      <div
                        className={`mb-2 p-2 rounded-lg border ${urgencyClasses.container}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <Clock className={`w-3.5 h-3.5 shrink-0 ${urgencyClasses.icon}`} />
                            <span className="text-xs font-medium text-gray-900 dark:text-slate-100">
                              {formatOpportunityDeadlineDate(opp.deadline)}
                            </span>
                          </div>
                          <span className={`text-xs font-medium whitespace-nowrap ${urgencyClasses.days}`}>
                            {formatDeadlineDaysLabel(opp.daysUntil)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-theme-text-secondary mb-2">
                        <Clock className="w-3 h-3" />
                        <span>{formatDeadlineDaysLabel(opp.daysUntil)}</span>
                      </div>
                    )}

                    {opp.assignedBd && (
                      <div className="flex items-center gap-1 text-xs text-theme-text-secondary">
                        <User className="w-3 h-3" />
                        <span className="truncate">{opp.assignedBd}</span>
                      </div>
                    )}

                    <div className="mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex justify-end">
                      <ChevronRight className="w-4 h-4 text-theme-text-secondary" />
                    </div>
                  </div>
                );
              })}

                {column.opportunities.length === 0 && column.id === 'new' && (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-center dark:border-slate-700 dark:bg-slate-900">
                    <Inbox className="mx-auto mb-2 h-5 w-5 text-slate-500" aria-hidden="true" />
                    <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      No tracked tenders
                    </p>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
                      Unmarked catalog rows stay on Cards and List. Track a tender to put it on this board.
                    </p>
                    {onViewList && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-4"
                        onClick={onViewList}
                      >
                        <List className="mr-1.5 h-4 w-4" aria-hidden="true" />
                        Open List View
                      </Button>
                    )}
                  </div>
                )}
                {column.opportunities.length === 0 && column.id !== 'new' && (
                  <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm text-center dark:border-slate-700 dark:bg-slate-900">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      No cards in this stage
                    </p>
                  </div>
                )}
                {column.opportunities.length > (visibleByColumn[column.id] ?? columnPageSize) && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="w-full"
                    onClick={() =>
                      setVisibleByColumn((prev) => ({
                        ...prev,
                        [column.id]: (prev[column.id] ?? columnPageSize) + columnPageSize,
                      }))
                    }
                  >
                    Load More
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
